// Load CommandProvider instances from YAML configuration (Issue #55).

import { readFile, stat } from "node:fs/promises";
import { join } from "node:path";

import { parse as parseYaml } from "yaml";

import {
  CommandProvider,
  type CommandProviderConfig,
} from "./command-provider.js";
import type { InsightProvider } from "./types.js";

interface ExternalProviderEntry {
  name: string;
  command: string;
  timeout?: number;
  env?: Record<string, string>;
  cwd?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseProviderEntry(entry: unknown): ExternalProviderEntry | null {
  if (!isRecord(entry)) return null;
  if (typeof entry.name !== "string" || typeof entry.command !== "string") {
    return null;
  }

  let env: Record<string, string> | undefined;
  if (isRecord(entry.env)) {
    env = {};
    for (const [key, value] of Object.entries(entry.env)) {
      if (typeof value === "string") env[key] = value;
    }
  }

  return {
    name: entry.name,
    command: entry.command,
    timeout: typeof entry.timeout === "number" ? entry.timeout : undefined,
    env,
    cwd: typeof entry.cwd === "string" ? entry.cwd : undefined,
  };
}

/**
 * Load external insight providers from YAML configuration.
 *
 * Reads `external_providers` from `<projectRoot>/artifact-contracts.yaml`
 * unless `configPath` is given. Returns `[]` when the file is missing,
 * unparsable, or has no `external_providers` section.
 */
export async function loadExternalProviders(
  projectRoot: string,
  configPath?: string,
): Promise<InsightProvider[]> {
  const yamlPath = configPath ?? join(projectRoot, "artifact-contracts.yaml");

  try {
    const info = await stat(yamlPath);
    if (!info.isFile()) return [];
  } catch {
    return [];
  }

  let raw: string;
  try {
    raw = await readFile(yamlPath, "utf8");
  } catch {
    return [];
  }

  let doc: unknown;
  try {
    doc = parseYaml(raw);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn(
      `[warn] Failed to parse config for external providers (${yamlPath}): ${message}`,
    );
    return [];
  }

  if (!isRecord(doc)) return [];

  const providersRaw = doc["external_providers"];
  if (!Array.isArray(providersRaw)) return [];

  const providers: InsightProvider[] = [];
  for (const entry of providersRaw) {
    const parsed = parseProviderEntry(entry);
    if (!parsed) {
      console.warn(
        `[warn] Skipping invalid external_providers entry in ${yamlPath}`,
      );
      continue;
    }

    const config: CommandProviderConfig = {
      name: parsed.name,
      command: parsed.command,
      ...(parsed.timeout !== undefined ? { timeout: parsed.timeout } : {}),
      ...(parsed.env ? { env: parsed.env } : {}),
      ...(parsed.cwd ? { cwd: parsed.cwd } : {}),
    };
    providers.push(new CommandProvider(config));
  }

  return providers;
}
